/**
 * Merch planner — el motor puro (sin React, sin API, sin imports).
 *
 * Nace de la antigua calculadora (`calc.ts`, CH-09) y de `ox-calc-xotski/src/engine/giveaway.ts`
 * (7b613dc, calc.xotski.com). Cada salida enseña qué entrada la movió; si falta un dato,
 * la salida es un pendiente con nombre, nunca un cero ni una suposición.
 *
 *   totalGuests = eventos × invitados por evento
 *   players     = round(totalGuests × share who play)
 *   claims      = round(players × claim rate)
 *   toOrder     = ⌈claims × (1 + buffer)⌉            (ceilSafe, giveaway.ts:233)
 *   leftovers   = toOrder − claims
 *   chance_i    = Σ_estación  estaciones_s × (w_i,s / W_s) / N    con w = (6 − rareza)²
 *   planned_i   = reparto por resto mayor de `claims` según chance
 *   toOrder_i   = reparto por resto mayor de `toOrder` según chance
 *   restockAt_i = max(1, ⌈toOrder_i ÷ eventos ÷ estaciones con ese premio ÷ horas⌉)  (guía, no se guarda)
 *   capacidad   = giveaway.ts:254-282
 *
 * COPIA C8 DE LA CURVA (reversible). `rarityWeight` repite a propósito la curva del servidor
 * (`pesoDe`, ox-platform `xotski.data-source.ts:243-244`) y de `pickPrize.ts:25`: unirlas en una
 * sola función exportada desde el juego es decisión de Daniel (CA-3D). Mientras tanto
 * `scripts/merch-planner-check.mjs` compara las tres en cada ejecución. Para revertir cuando
 * Daniel diga sí: exportar `rarityWeight` desde `pickPrize.ts`, importarla aquí y borrar esta copia.
 */

export interface PlanInput {
  /** Entero ≥ 1. */
  events: number | null;
  /** Invitados POR EVENTO, > 0. */
  guests: number | null;
  /** 0 < x ≤ 1. */
  shareWhoPlay: number | null;
  /** 0 < x ≤ 1. */
  claimRate: number | null;
  /** ≥ 0. */
  buffer: number | null;
  /** > 0 — sólo reposición y capacidad. */
  hours: number | null;
  /** 0 < x ≤ 60 — sólo capacidad. */
  minutesPerHandout: number | null;
}

export interface PlanSlot {
  stockId: number;
  name: string;
  weight: number;
}

/** Una estación real (stationId, stations = 1) o el catálogo de la marca (stationId null, stations = las de la entrada). */
export interface PlanPool {
  stationId: number | null;
  stations: number;
  slots: PlanSlot[];
}

export type PlanNeed =
  | 'events'
  | 'guests'
  | 'shareWhoPlay'
  | 'claimRate'
  | 'buffer'
  | 'stations'
  | 'items'
  | 'hours'
  | 'minutesPerHandout';

export type Sub<T> = { state: 'PENDING'; needs: PlanNeed[] } | ({ state: 'MEASURED' } & T);

export interface Throughput {
  perPointPerHour: number;
  points: number;
  capacityPerHour: number;
  peakPlayersPerHour: number;
  pointsNeeded: number;
  extraPoints: number;
  covered: boolean;
}

export interface PlanRow {
  stockId: number;
  name: string;
  /** «rarity n» o «rarity a–b». */
  weightMin: number;
  weightMax: number;
  /** 0–1. */
  chance: number;
  /** Canjes previstos (entero). */
  planned: number;
  /** Pedido (entero). */
  toOrder: number;
  /** ⌈toOrder ÷ events⌉. */
  perEvent: number;
  stationsHolding: number;
  /** null sin `hours`. */
  restockAt: number | null;
}

export type PlanResult =
  | { state: 'PENDING'; needs: PlanNeed[] }
  | {
      state: 'MEASURED';
      totalGuests: number;
      players: number;
      claims: number;
      toOrder: number;
      leftovers: number;
      rows: PlanRow[];
      restock: Sub<object>;
      capacity: Sub<Throughput>;
      working: {
        events: number;
        guests: number;
        shareWhoPlay: number;
        claimRate: number;
        buffer: number;
        stations: number;
        hours: number | null;
      };
    };

/** Los valores por defecto de calc.xotski.com (`ox-calc-xotski/src/App.tsx:42-53` @7b613dc). */
export const WEBSITE_DEFAULTS = {
  events: 5,
  guests: 300,
  shareWhoPlay: 0.4,
  claimRate: 0.9,
  buffer: 0.15,
  hours: 3,
  minutesPerHandout: 2,
  stations: 1,
} as const;

/** Copia C8 de la curva del juego (= `pesoDe` del servidor). Reversible: ver la cabecera. */
export function rarityWeight(rating?: number | null): number {
  const r = Math.max(1, Math.min(5, rating ?? 3));
  return (6 - r) ** 2;
}

/** `Math.ceil` que ignora el ruido de coma flotante (100 × 1,1 = 110.00000000000001). `giveaway.ts:233`. */
export function ceilSafe(n: number): number {
  return Math.ceil(Math.round(n * 1e6) / 1e6);
}

const round6 = (n: number): number => Math.round(n * 1e6) / 1e6;

/**
 * Reparte `total` (entero) en enteros proporcionales a `shares` por resto mayor:
 * la suma es siempre `total`. Empate: mayor parte, luego menor índice.
 */
export function apportion(total: number, shares: number[]): number[] {
  const out = shares.map(() => 0);
  const sum = shares.reduce((a, b) => a + b, 0);
  if (!(total > 0) || !(sum > 0)) return out;
  const quotas = shares.map((s) => round6((total * s) / sum));
  const base = quotas.map((q) => Math.floor(q));
  let rest = total - base.reduce((a, b) => a + b, 0);
  const frac = quotas.map((q, i) => q - base[i]);
  const order = shares.map((_, i) => i).sort((a, b) => frac[b] - frac[a] || shares[b] - shares[a] || a - b);
  for (let k = 0; rest > 0 && order.length > 0; k = (k + 1) % order.length) {
    base[order[k]] += 1;
    rest -= 1;
  }
  // Salvaguarda (el redondeo a 6 decimales nunca debería pasarse): quita de las menores partes.
  for (let k = order.length - 1; rest < 0 && k >= 0; k = k === 0 ? order.length - 1 : k - 1) {
    if (base[order[k]] > 0) {
      base[order[k]] -= 1;
      rest += 1;
    }
  }
  return base;
}

/** Peso de una fila del juego: cadencia pesa 1; si no, la curva con `fila.rating ?? merch.rating`. */
export function slotWeight(
  row: { rating?: number | null; prizeMode?: 'weight' | 'cooldown' | null },
  stockRating?: number | null,
): number {
  return row.prizeMode === 'cooldown' ? 1 : rarityWeight(row.rating ?? stockRating ?? null);
}

const isPosInt = (n: number | null): n is number => n != null && Number.isInteger(n) && n >= 1;

export function plan(input: PlanInput, pools: PlanPool[]): PlanResult {
  const needs: PlanNeed[] = [];
  if (!isPosInt(input.events)) needs.push('events');
  if (input.guests == null || !Number.isFinite(input.guests) || !(input.guests > 0)) needs.push('guests');
  if (input.shareWhoPlay == null || !(input.shareWhoPlay > 0) || input.shareWhoPlay > 1) needs.push('shareWhoPlay');
  if (input.claimRate == null || !(input.claimRate > 0) || input.claimRate > 1) needs.push('claimRate');
  if (input.buffer == null || !Number.isFinite(input.buffer) || input.buffer < 0) needs.push('buffer');
  if (pools.some((p) => !isPosInt(p.stations))) needs.push('stations');
  const poolWeight = (p: PlanPool) => p.slots.reduce((a, s) => a + s.weight, 0);
  const playable = pools.filter((p) => poolWeight(p) > 0);
  if (playable.length === 0) needs.push('items');
  if (needs.length > 0) return { state: 'PENDING', needs };

  const events = input.events as number;
  const guests = input.guests as number;
  const shareWhoPlay = input.shareWhoPlay as number;
  const claimRate = input.claimRate as number;
  const buffer = input.buffer as number;
  const hours = input.hours != null && input.hours > 0 ? input.hours : null;

  const totalGuests = events * guests;
  const players = Math.round(totalGuests * shareWhoPlay);
  const claims = Math.round(players * claimRate);
  const toOrder = ceilSafe(claims * (1 + buffer));
  const leftovers = toOrder - claims;

  const N = playable.reduce((a, p) => a + p.stations, 0);
  interface Acc {
    name: string;
    chance: number;
    min: number;
    max: number;
    holding: number;
  }
  const acc = new Map<number, Acc>();
  for (const p of playable) {
    const W = poolWeight(p);
    const wByStock = new Map<number, { name: string; w: number; min: number; max: number }>();
    for (const s of p.slots) {
      const cur = wByStock.get(s.stockId);
      if (cur) {
        cur.w += s.weight;
        cur.min = Math.min(cur.min, s.weight);
        cur.max = Math.max(cur.max, s.weight);
      } else wByStock.set(s.stockId, { name: s.name, w: s.weight, min: s.weight, max: s.weight });
    }
    for (const [stockId, v] of wByStock) {
      if (!(v.w > 0)) continue;
      const a = acc.get(stockId) ?? { name: v.name, chance: 0, min: v.min, max: v.max, holding: 0 };
      a.chance += (p.stations * (v.w / W)) / N;
      a.min = Math.min(a.min, v.min);
      a.max = Math.max(a.max, v.max);
      a.holding += p.stations;
      acc.set(stockId, a);
    }
  }
  const sorted = [...acc.entries()].sort((x, y) => y[1].chance - x[1].chance || x[1].name.localeCompare(y[1].name));
  const chances = sorted.map(([, a]) => a.chance);
  const planned = apportion(claims, chances);
  const ordered = apportion(toOrder, chances);
  const rows: PlanRow[] = sorted.map(([stockId, a], i) => ({
    stockId,
    name: a.name,
    weightMin: a.min,
    weightMax: a.max,
    chance: a.chance,
    planned: planned[i],
    toOrder: ordered[i],
    perEvent: ceilSafe(ordered[i] / events),
    stationsHolding: a.holding,
    restockAt:
      hours == null ? null : ordered[i] > 0 ? Math.max(1, ceilSafe(ordered[i] / events / a.holding / hours)) : 0,
  }));

  const restock: Sub<object> = hours == null ? { state: 'PENDING', needs: ['hours'] } : { state: 'MEASURED' };

  const capNeeds: PlanNeed[] = [];
  if (hours == null) capNeeds.push('hours');
  const minutes = input.minutesPerHandout;
  if (minutes == null || !(minutes > 0) || minutes > 60) capNeeds.push('minutesPerHandout');
  let capacity: Sub<Throughput>;
  if (capNeeds.length > 0 || hours == null || minutes == null) {
    capacity = { state: 'PENDING', needs: capNeeds };
  } else {
    const perPointPerHour = Math.floor(60 / minutes);
    const points = N;
    const capacityPerHour = points * perPointPerHour;
    const peakPlayersPerHour = ceilSafe(Math.round(guests * shareWhoPlay) / hours);
    const pointsNeeded = perPointPerHour > 0 ? ceilSafe(peakPlayersPerHour / perPointPerHour) : 0;
    capacity = {
      state: 'MEASURED',
      perPointPerHour,
      points,
      capacityPerHour,
      peakPlayersPerHour,
      pointsNeeded,
      extraPoints: Math.max(0, pointsNeeded - points),
      covered: capacityPerHour >= peakPlayersPerHour,
    };
  }

  return {
    state: 'MEASURED',
    totalGuests,
    players,
    claims,
    toOrder,
    leftovers,
    rows,
    restock,
    capacity,
    working: { events, guests, shareWhoPlay, claimRate, buffer, stations: N, hours },
  };
}

/**
 * Lo que se guarda en el evento: `toOrder` (hoy, el pedido con colchón) o `planned`
 * (los canjes previstos; CA-7D, espera a Daniel) por fila, y 0 para cada asignada que
 * no está en el plan. Una lista de asignadas vacía no genera ceros. Pendiente → [].
 */
export function expectedPayload(
  result: PlanResult,
  allocatedStockIds: readonly number[],
  basis: 'toOrder' | 'planned',
): Array<{ stockId: number; expected: number }> {
  if (result.state !== 'MEASURED') return [];
  const inPlan = new Set(result.rows.map((r) => r.stockId));
  const out = result.rows.map((r) => ({ stockId: r.stockId, expected: basis === 'toOrder' ? r.toOrder : r.planned }));
  for (const id of new Set(allocatedStockIds)) {
    if (!inPlan.has(id)) out.push({ stockId: id, expected: 0 });
  }
  return out;
}
